// src/components/apps/SUA/PaymentRecordsPage.jsx
import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  CircularProgress,
  Chip,
  TextField,
  InputAdornment,
  Stack,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Divider,
} from "@mui/material";
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Visibility as VisibilityIcon,
  GetApp as DownloadIcon,
} from "@mui/icons-material";
import { Link as RouterLink } from "react-router-dom";
import api from "../../../utils/api";

export default function PaymentRecordsPage() {
  const [rows, setRows] = useState([]);
  const [filteredRows, setFilteredRows] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);

  const fetchRecords = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get(`/payments/records/`);
      setRows(data || []);
      setFilteredRows(data || []);
    } catch (err) {
      console.error("Error fetching payment records:", err);
      setError(
        err?.response?.data?.detail ||
          err?.message ||
          "Failed to load records"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRecords();
  }, []);

  // Filter records based on search term
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredRows(rows);
      return;
    }

    const term = searchTerm.toLowerCase();
    const filtered = rows.filter(
      (r) =>
        r.payment_group_label?.toLowerCase().includes(term) ||
        r.mode?.toLowerCase().includes(term) ||
        r.id?.toString().includes(term) ||
        r.payment_group_id?.toString().includes(term)
    );
    setFilteredRows(filtered);
  }, [searchTerm, rows]);

  const handleViewDetail = (record) => {
    setSelectedRecord(record);
    setDetailOpen(true);
  };

  const handleCloseDetail = () => {
    setDetailOpen(false);
    setSelectedRecord(null);
  };

  const downloadContent = (content, filename, mime) => {
    const blob = new Blob([content], { type: mime });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleDownloadTxt = (record) => {
    if (record.preview_txt) {
      downloadContent(
        record.preview_txt,
        `sua_batch_${record.id}.txt`,
        "text/plain"
      );
    }
  };

  const handleDownloadCsv = (record) => {
    if (record.csv_raw) {
      downloadContent(
        record.csv_raw,
        `sua_batch_${record.id}.csv`,
        "text/csv"
      );
    }
  };

  // Calculate summary statistics
  const totalRecords = rows.length;
  const totalAmount = rows.reduce(
    (sum, r) => sum + parseFloat(r.total_amount || 0),
    0
  );
  const totalPayments = rows.reduce(
    (sum, r) => sum + parseInt(r.record_count || 0, 10),
    0
  );

  return (
    <Box component="main" sx={{ mt: 4 }}>
      <Box
        sx={{
          backgroundColor: "#f9f9f9",
          p: 3,
          borderRadius: 2,
          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
          mt: "50px",
          mb: 4,
        }}
      >
        {/* Header */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ mb: 3 }}
        >
          <Typography variant="h4" sx={{ color: "primary.main" }}>
            Payment Records
          </Typography>
          <Stack direction="row" spacing={1}>
            <Tooltip title="Refresh">
              <IconButton
                onClick={fetchRecords}
                disabled={loading}
                color="primary"
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <Button
              component={RouterLink}
              to="/sua-generator"
              variant="contained"
              size="small"
            >
              New Generation
            </Button>
          </Stack>
        </Stack>

        {/* Summary Cards */}
        {!loading && !error && (
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={4}>
              <Paper
                sx={{
                  p: 2,
                  textAlign: "center",
                  background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  color: "white",
                }}
              >
                <Typography variant="h4">{totalRecords}</Typography>
                <Typography variant="body2">Total Batches</Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Paper
                sx={{
                  p: 2,
                  textAlign: "center",
                  background: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
                  color: "white",
                }}
              >
                <Typography variant="h4">{totalPayments}</Typography>
                <Typography variant="body2">Total Payments</Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Paper
                sx={{
                  p: 2,
                  textAlign: "center",
                  background: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
                  color: "white",
                }}
              >
                <Typography variant="h4">
                  ${totalAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </Typography>
                <Typography variant="body2">Total Amount</Typography>
              </Paper>
            </Grid>
          </Grid>
        )}

        {/* Search Bar */}
        <TextField
          fullWidth
          size="small"
          placeholder="Search by ID, group, or mode..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ mb: 3 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Loading State */}
        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
            <CircularProgress />
          </Box>
        )}

        {/* Table */}
        {!loading && !error && (
          <TableContainer component={Paper} elevation={2}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: "primary.main" }}>
                  <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                    ID
                  </TableCell>
                  <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                    Payment Group
                  </TableCell>
                  <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                    Mode
                  </TableCell>
                  <TableCell sx={{ color: "white", fontWeight: "bold" }} align="right">
                    Count
                  </TableCell>
                  <TableCell sx={{ color: "white", fontWeight: "bold" }} align="right">
                    Total Amount
                  </TableCell>
                  <TableCell sx={{ color: "white", fontWeight: "bold" }}>
                    Created
                  </TableCell>
                  <TableCell sx={{ color: "white", fontWeight: "bold" }} align="center">
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredRows.length > 0 ? (
                  filteredRows.map((r) => (
                    <TableRow
                      key={r.id}
                      hover
                      sx={{
                        "&:hover": { backgroundColor: "action.hover" },
                      }}
                    >
                      <TableCell>
                        <Chip label={r.id} size="small" color="primary" />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {r.payment_group_label}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          ID: {r.payment_group_id}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={r.mode}
                          size="small"
                          variant="outlined"
                          color={r.mode === "Per Vendor" ? "success" : "info"}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight="medium">
                          {r.record_count}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight="medium">
                          ${parseFloat(r.total_amount || 0).toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {new Date(r.created_at).toLocaleDateString()}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {new Date(r.created_at).toLocaleTimeString()}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Stack direction="row" spacing={1} justifyContent="center">
                          <Tooltip title="View Details">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleViewDetail(r)}
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {r.preview_txt && (
                            <Tooltip title="Download TXT">
                              <IconButton
                                size="small"
                                color="secondary"
                                onClick={() => handleDownloadTxt(r)}
                              >
                                <DownloadIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                      <Typography variant="body1" color="text.secondary">
                        {searchTerm
                          ? "No records match your search."
                          : "No payment records yet. Generate your first batch!"}
                      </Typography>
                      {!searchTerm && (
                        <Button
                          component={RouterLink}
                          to="/sua-generator"
                          variant="contained"
                          sx={{ mt: 2 }}
                        >
                          Create First Record
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      {/* Detail Dialog */}
      <Dialog
        open={detailOpen}
        onClose={handleCloseDetail}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Payment Record Details</Typography>
            <Chip label={`ID: ${selectedRecord?.id}`} color="primary" />
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          {selectedRecord && (
            <Stack spacing={2}>
              {/* Basic Info */}
              <Box>
                <Typography variant="subtitle2" color="primary" gutterBottom>
                  Basic Information
                </Typography>
                <Divider sx={{ mb: 1 }} />
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Payment Group
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {selectedRecord.payment_group_label}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Group ID
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {selectedRecord.payment_group_id}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Mode
                    </Typography>
                    <Chip
                      label={selectedRecord.mode}
                      size="small"
                      color={selectedRecord.mode === "Per Vendor" ? "success" : "info"}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Record Count
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {selectedRecord.record_count}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Total Amount
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      ${parseFloat(selectedRecord.total_amount || 0).toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Created At
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {new Date(selectedRecord.created_at).toLocaleString()}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>

              {/* SHA256 */}
              {selectedRecord.sha256 && (
                <Box>
                  <Typography variant="subtitle2" color="primary" gutterBottom>
                    Checksum
                  </Typography>
                  <Divider sx={{ mb: 1 }} />
                  <Typography
                    variant="body2"
                    sx={{
                      fontFamily: "monospace",
                      wordBreak: "break-all",
                      backgroundColor: "grey.100",
                      p: 1,
                      borderRadius: 1,
                    }}
                  >
                    {selectedRecord.sha256}
                  </Typography>
                </Box>
              )}

              {/* Preview */}
              {selectedRecord.preview_txt && (
                <Box>
                  <Typography variant="subtitle2" color="primary" gutterBottom>
                    Preview (First 500 characters)
                  </Typography>
                  <Divider sx={{ mb: 1 }} />
                  <TextField
                    fullWidth
                    multiline
                    rows={6}
                    value={selectedRecord.preview_txt.substring(0, 500)}
                    InputProps={{ readOnly: true }}
                    sx={{
                      fontFamily: "monospace",
                      "& .MuiInputBase-input": {
                        fontFamily: "monospace",
                        fontSize: "0.75rem",
                      },
                    }}
                  />
                </Box>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          {selectedRecord?.csv_raw && (
            <Button
              startIcon={<DownloadIcon />}
              onClick={() => handleDownloadCsv(selectedRecord)}
            >
              Download CSV
            </Button>
          )}
          {selectedRecord?.preview_txt && (
            <Button
              startIcon={<DownloadIcon />}
              onClick={() => handleDownloadTxt(selectedRecord)}
              variant="contained"
            >
              Download TXT
            </Button>
          )}
          <Button onClick={handleCloseDetail}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
